import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { css } from '@emotion/core'
import { func, string } from 'prop-types'
import { readableTextColor } from '../../constants/activityCoverColors'
import { Z_INDICES } from '../../constants/zIndices'

export const SLIDE_DURATION_MS = 300
export const NAVIGATE_AT_FRACTION = 0.6
const DOT_CYCLE_MS = 1400

const dotsCss = css`
  @keyframes activity-loading-dot {
    0%,
    80%,
    100% {
      opacity: 0.25;
    }
    40% {
      opacity: 1;
    }
  }

  > span {
    animation: activity-loading-dot ${DOT_CYCLE_MS}ms ease-in-out infinite;
  }

  > span:nth-of-type(2) {
    animation-delay: ${Math.round(DOT_CYCLE_MS * 0.16)}ms;
  }

  > span:nth-of-type(3) {
    animation-delay: ${Math.round(DOT_CYCLE_MS * 0.32)}ms;
  }

  @media (prefers-reduced-motion: reduce) {
    > span {
      animation: none;
      opacity: 1;
    }
  }
`

const labelCss = css`
  font-family: var(
    --font-families-headings,
    'HeritageHeadings:Sans',
    'HanaMinBFont',
    ui-sans-serif,
    system-ui,
    sans-serif,
    'Tofu'
  );
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;

  /* Anchored rather than centred by the cover, and matched to discovery-react's
     activityLoadingText.js. Centring follows the viewport, which changes height whenever browser
     chrome animates in or out, sliding the label to a new spot mid-transition. lvh is the height with chrome
     hidden, which is the state that persists, so the label is centred where it ends up rather than
     where it started; the plain % above it is the fallback. */
  position: absolute;
  inset-inline: 32px;
  top: 50%;
  top: 50lvh;
  transform: translateY(-50%);
`

const coverCss = (color, raised) => css`
  position: fixed;
  inset: 0;
  z-index: ${Z_INDICES.ACTIVITY_LAUNCH_COVER};
  background-color: ${color};
  /* Derived: not every activity's colour is dark, and a white label vanishes on the light ones. */
  color: ${readableTextColor(color)};
  text-align: center;
  transform: translateY(${raised ? '0%' : '100%'});
  transition: transform ${SLIDE_DURATION_MS}ms ease-out;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/**
 * Slides a full-screen color up over the app, then navigates to the activity partway through.
 *
 * When used for navigating to discovery-react activities, matching the color to the background
 * of the discovery-react activity makes the hand-off to discovery-react invisible.
 *
 */
export default function ActivityLaunchCover({ color, label, onNavigate, onDismiss }) {
  const [raised, setRaised] = useState(false)
  const coverRef = useRef(null)

  const onNavigateRef = useRef(onNavigate)
  onNavigateRef.current = onNavigate
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss

  useEffect(() => {
    const appRoot = document.getElementById('root')
    const release = () => appRoot?.removeAttribute('inert')
    appRoot?.setAttribute('inert', '')

    const frame = requestAnimationFrame(() => setRaised(true))
    let handedOff = false
    const timer = setTimeout(() => {
      handedOff = true
      onNavigateRef.current?.()
    }, SLIDE_DURATION_MS * NAVIGATE_AT_FRACTION)

    const dismiss = () => {
      if (coverRef.current) coverRef.current.style.display = 'none'
      release()
      onDismissRef.current?.()
    }
    const dismissOnVisibilityChange = () => {
      if (handedOff) dismiss()
    }
    window.addEventListener('pagehide', dismiss)
    window.addEventListener('pageshow', dismiss)
    document.addEventListener('visibilitychange', dismissOnVisibilityChange)

    return () => {
      release()
      cancelAnimationFrame(frame)
      clearTimeout(timer)
      window.removeEventListener('pagehide', dismiss)
      window.removeEventListener('pageshow', dismiss)
      document.removeEventListener('visibilitychange', dismissOnVisibilityChange)
    }
  }, [])

  return createPortal(
    <div ref={coverRef} css={coverCss(color, raised)} data-testid="activity-launch-cover">
      {/* Carried into discovery-react via the coverLabel param so the same words stay on screen,
          in the same place, right through the document swap. */}
      <output aria-live="polite" css={labelCss}>
        {label}
        <span css={dotsCss} aria-hidden="true">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      </output>
    </div>,
    document.body
  )
}

ActivityLaunchCover.propTypes = {
  /** Colour the destination activity paints while it loads. */
  color: string.isRequired,
  /** Already-localized loading text, also forwarded to the destination so it does not change. */
  label: string,
  /** Called partway through the slide, once the screen is opaque enough to navigate behind. */
  onNavigate: func.isRequired,
  /** Called when this document is restored from the back/forward cache, so the cover can be torn down. */
  onDismiss: func,
}
